import React, { useState } from 'react';

import { Box, CircularProgress, InputBase, Slider, styled } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { truthyOrElse } from '@/components/NewUI/utils/truthyOrElse';

import { BLACK, SCOUT_BLUE, WHITE } from '../../../../ThemeContext/constants';
import { sharedClasses } from '../../CustomUIElements/sharedClasses';
import { handleAPIRequest } from '../../Utilities/handleAPIRequest';

import type { SnackbarInput } from '@/components/NewUI/ComponentsLibrary';

const DEFAULT_RADIUS = 2;
const DEFAULT_MIN_RADIUS = 1;
const DEFAULT_MAX_RADIUS = 20;
const SEARCH_DELAY = 900;

const sliderContainer = {
  fontFamily: 'Source Sans Pro',
  fontSize: '10px',
  color: SCOUT_BLUE,
  padding: '6px 9px 0px 9px'
};

const adornmentContainer = {
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};

const StyledInput = styled(InputBase)(() => ({
  width: '100%',
  height: '41px',
  border: '1px solid #DADEE0',
  backgroundColor: WHITE,
  borderRadius: '6px',
  padding: '6px 22px 6px 6px',
  display: 'flex',
  marginTop: '3px',
  '&:focus-within': {
    borderColor: '#77C2BE',
    boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)'
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Source Sans Pro',
    fontSize: '12px',
    color: BLACK,
    paddingLeft: '6px',
    height: '26px'
  },
  '& input': {
    '&::placeholder': {
      color: BLACK
    }
  }
}));

const RadiusSearchFilter = ({
  setPostcodes,
  setSnackbarState
}: {
  setPostcodes: (postcodes: string[]) => void;
  setSnackbarState: (snackbarState: SnackbarInput) => void;
}) => {
  const [inputPostcode, setInputPostcode] = useState('');
  const [inputRadius, setInputRadius] = useState(DEFAULT_RADIUS);
  const [isLoading, setIsLoading] = useState(false);

  const handleRadiusSearch = useDebouncedCallback((postcode, radius) => {
    if (!postcode && !inputPostcode) return;
    handleAPIRequest({
      method: 'POST',
      url: `/api/v4/searches/find_nearby_postcodes`,
      body: {
        postcode: truthyOrElse(postcode, () => inputPostcode),
        radius: truthyOrElse(radius, () => inputRadius)
      },
      setLoaderState: setIsLoading,
      setSnackbarState,
      successMessage: { show: false },
      onSuccess: (response: { postal_codes: string[] }) => {
        setPostcodes(response.postal_codes);
      }
    });
  }, SEARCH_DELAY);

  return (
    <Box>
      <StyledInput
        placeholder="Input a postcode"
        value={inputPostcode}
        endAdornment={
          isLoading ? (
            <Box sx={adornmentContainer}>
              <CircularProgress size={18} />
            </Box>
          ) : null
        }
        onChange={(event) => {
          if (isLoading) return;
          setInputPostcode(event.target.value);
          handleRadiusSearch(event.target.value, inputRadius);
        }}
      />
      <Box id="radius-search-filter" sx={sliderContainer}>
        Within
        <Box>
          <Slider
            aria-label="Radius search slider"
            getAriaValueText={(value: number) => `${value}km`}
            max={DEFAULT_MAX_RADIUS}
            min={DEFAULT_MIN_RADIUS}
            sx={sharedClasses.ratingSlider}
            value={inputRadius}
            valueLabelDisplay="on"
            valueLabelFormat={(value: number) => `${value}km`}
            onChange={(event, value) => {
              if (isLoading) return;
              setInputRadius(value);
              handleRadiusSearch(inputPostcode, value);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RadiusSearchFilter;
